import React, { useState, useEffect } from 'react';
import { FiEdit2, FiTrash2, FiLock, FiUsers, FiSearch } from 'react-icons/fi'; // Feather icons for cleaner look
import config from '../../config/config';

export function ViewUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.baseUrl}/api/users/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch users');

      const data = await response.json();
      setUsers(data);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 md:p-8">
      {/* Page Header */}
      <div className="max-w-7xl mx-auto mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-800 tracking-tight flex items-center gap-3">
            <div className="bg-indigo-600 p-2 rounded-xl text-white shadow-lg shadow-indigo-200">
              <FiUsers size={24} />
            </div>
            User Management
          </h1>
          <p className="text-slate-500 mt-2 ml-14">View, edit and manage system access levels.</p>
        </div>

        {/* Search Bar Placeholder */}
        <div className="relative group">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
          <input
            type="text"
            placeholder="Search users..."
            className="pl-12 pr-4 py-3 rounded-2xl bg-white border border-slate-200 focus:border-indigo-500 outline-none w-full md:w-80 shadow-sm transition-all text-sm font-medium"
          />
        </div>
      </div>

      {/* Table Container */}
      <div className="max-w-7xl mx-auto bg-white rounded-[2rem] shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-100">
                <th className="px-8 py-5 text-[11px] font-black text-slate-500 uppercase tracking-widest">ID</th>
                <th className="px-6 py-5 text-[11px] font-black text-slate-500 uppercase tracking-widest">User Details</th>
                <th className="px-6 py-5 text-[11px] font-black text-slate-500 uppercase tracking-widest text-center">Role</th>
                <th className="px-6 py-5 text-[11px] font-black text-slate-500 uppercase tracking-widest">Created Date</th>
                <th className="px-6 py-5 text-[11px] font-black text-slate-500 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr>
                  <td colSpan="5" className="py-20 text-center text-slate-400 font-medium">Loading user data...</td>
                </tr>
              ) : users.map((user) => (
                <tr key={user.id} className="hover:bg-indigo-50/30 transition-colors group">
                  <td className="px-8 py-5 text-sm font-bold text-slate-400">#{user.id}</td>
                  <td className="px-6 py-5">
                    <div className="flex flex-col">
                      <span className="text-sm font-black text-slate-700">{user.name || 'No Name'}</span>
                      <span className="text-xs font-medium text-slate-400">{user.email}</span>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <span className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider ${user.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                      user.role === 'superadmin' ? 'bg-rose-100 text-rose-700' :
                        user.role === 'moderator' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-600'
                      }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-5 text-sm font-medium text-slate-500">
                    {new Date(user.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex justify-end gap-2">
                      <button className="p-2.5 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all shadow-sm" title="Edit User">
                        <FiEdit2 size={16} />
                      </button>
                      <button className="p-2.5 rounded-xl bg-amber-50 text-amber-600 hover:bg-amber-600 hover:text-white transition-all shadow-sm" title="Change Password">
                        <FiLock size={16} />
                      </button>
                      <button className="p-2.5 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all shadow-sm" title="Delete User">
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}